import { useForm } from 'react-hook-form';
import { NJButton, NJHeading } from '@engie-group/fluid-design-system-react';
import { RightSideModal } from './RightSideModalFixed';
import type { StorageNetworkFormData } from './types';

interface StorageNetworkEditModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialData?: Partial<StorageNetworkFormData>;
    onSubmit: (data: StorageNetworkFormData) => void;
}

const RETURN_NODE_OPTIONS = Array.from({ length: 30 }, (_, i) => `NR${String(i + 1).padStart(4, '0')}`);
const SUPPLY_NODE_OPTIONS = Array.from({ length: 30 }, (_, i) => `ND${String(i + 1).padStart(4, '0')}`);

const FIELD_STYLE: React.CSSProperties = {
    width: '100%', minHeight: 40, padding: '10px 12px',
    border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
    borderRadius: 4, fontSize: 14, boxSizing: 'border-box',
};

const LABEL_STYLE: React.CSSProperties = { display: 'block', fontSize: 14, marginBottom: 6 };

const CONTRACT_OPTIONS = [
    'Contrat elec Auber',
    'Contrat elec Canada',
    'Contrat elec DLTM',
    'Contrat elec Etoile',
    'Contrat elec Halles',
    'Contrat elec Opera',
];

export function StorageNetworkEditModal({
    isOpen,
    onClose,
    initialData,
    onSubmit,
}: StorageNetworkEditModalProps) {
    const { register, handleSubmit, reset } = useForm<StorageNetworkFormData>({
        defaultValues: initialData ?? {
            identifier: '',
            name: '',
            electricityContract: 'Energy_Forum',
            powerLoss: 0,
            temperatureEvolutionRate: 0,
            volume: 500,
            maxChargeFlow: 0.15,
            maxDischargeFlow: 0.15,
            chargePrice: 0,
            dischargePrice: 0,
            externalTemperature: 0,
            returnNode: '',
            supplyNode: '',
        },
    });

    const handleFormSubmit = (data: StorageNetworkFormData) => {
        if (!initialData) {
            data.maxDischargeFlow = data.maxChargeFlow;
        }
        onSubmit(data);
        reset();
    };

    // Mode création : formulaire simplifié
    if (!initialData) {
        return (
            <RightSideModal
                isOpen={isOpen}
                onClose={onClose}
                title="Créer stockage réseau"
                metadata={`Créé par PROTOTYPER le ${new Date().toLocaleDateString('fr-FR')}`}
                footer={
                    <>
                        <NJButton label="Annuler" emphasis="subtle" onClick={onClose} />
                        <NJButton label="Valider" emphasis="bold" onClick={handleSubmit(handleFormSubmit)} />
                    </>
                }
            >
                <div style={{ marginBottom: 16, fontSize: 12, color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}>
                    * Champs obligatoires
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Nom *</label>
                    <input {...register('name', { required: true })} placeholder="Stockage 1" style={FIELD_STYLE} />
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Nœud de retour *</label>
                    <select {...register('returnNode', { required: true })} style={FIELD_STYLE}>
                        <option value="">-- Sélectionner --</option>
                        {RETURN_NODE_OPTIONS.map((node) => (
                            <option key={node} value={node}>{node}</option>
                        ))}
                    </select>
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Nœud de départ *</label>
                    <select {...register('supplyNode', { required: true })} style={FIELD_STYLE}>
                        <option value="">-- Sélectionner --</option>
                        {SUPPLY_NODE_OPTIONS.map((node) => (
                            <option key={node} value={node}>{node}</option>
                        ))}
                    </select>
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Contrat d'électricité auxiliaire *</label>
                    <select {...register('electricityContract', { required: true })} style={FIELD_STYLE}>
                        <option value="">-- Sélectionner --</option>
                        {CONTRACT_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Volume du stockage (m³) *</label>
                    <input type="number" step="0.1" {...register('volume', { required: true })} placeholder="500" style={FIELD_STYLE} />
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Débit maximal (m³/s) *</label>
                    <input type="number" step="0.01" {...register('maxChargeFlow', { required: true })} placeholder="0.15" style={FIELD_STYLE} />
                </div>
            </RightSideModal>
        );
    }

    // Mode modification : formulaire complet avec sections
    return (
        <RightSideModal
            isOpen={isOpen}
            onClose={onClose}
            title="Éditer stockage réseau"
            metadata={`Modifié par PROTOTYPER le ${new Date().toLocaleDateString('fr-FR')}`}
            footer={
                <>
                    <NJButton label="Annuler" emphasis="subtle" onClick={onClose} />
                    <NJButton label="Valider" emphasis="bold" onClick={handleSubmit(handleFormSubmit)} />
                </>
            }
        >
            <div style={{ marginBottom: 16, fontSize: 12, color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}>
                * Champs obligatoires
            </div>

            {/* Section: Informations générales */}
            <div style={{ marginBottom: 24 }}>
                <NJHeading as="h4" scale="sm" style={{ marginBottom: 16 }}>
                    Informations générales
                </NJHeading>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Identifiant</label>
                    <input
                        {...register('identifier')}
                        readOnly
                        placeholder="STOCK-NET-001"
                        style={{ ...FIELD_STYLE, backgroundColor: 'var(--nj-semantic-color-surface-disabled)' }}
                    />
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Nom *</label>
                    <input {...register('name', { required: true })} placeholder="Stockage 1" style={FIELD_STYLE} />
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Contrat d'électricité auxiliaire</label>
                    <select {...register('electricityContract')} style={FIELD_STYLE}>
                        <option value="">-- Sélectionner --</option>
                        {CONTRACT_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>
            </div>

            {/* Section: Paramètres techniques */}
            <div style={{ marginBottom: 24 }}>
                <NJHeading as="h4" scale="sm" style={{ marginBottom: 16 }}>
                    Paramètres techniques
                </NJHeading>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Volume (m³)</label>
                    <input type="number" step="0.1" {...register('volume')} placeholder="500" style={FIELD_STYLE} />
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Débit maximal de charge (m³/s)</label>
                    <input type="number" step="0.01" {...register('maxChargeFlow')} placeholder="0.15" style={FIELD_STYLE} />
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Débit maximal de décharge (m³/s)</label>
                    <input type="number" step="0.01" {...register('maxDischargeFlow')} placeholder="0.15" style={FIELD_STYLE} />
                </div>
            </div>

            {/* Section: Perte thermique */}
            <div style={{ marginBottom: 24 }}>
                <NJHeading as="h4" scale="sm" style={{ marginBottom: 16 }}>
                    Perte thermique
                </NJHeading>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Puissance de déperdition (W)</label>
                    <input type="number" {...register('powerLoss')} placeholder="0" style={FIELD_STYLE} />
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Température extérieure (K)</label>
                    <input type="number" step="0.1" {...register('externalTemperature')} placeholder="0" style={FIELD_STYLE} />
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Taux d'évolution de la température (1/s)</label>
                    <input type="number" step="0.0001" {...register('temperatureEvolutionRate')} placeholder="0" style={FIELD_STYLE} />
                </div>
            </div>
        </RightSideModal>
    );
}
